import { useEffect, useRef } from "react";
import { takePendingFirstMessage } from "./pendingFirstMessage";

interface DeliveryGates {
  // Resuming an in-flight run; that mount owns the conversation, not this send.
  resumeInFlight: boolean;
  // Connector grants / MCP-guidelines acceptance still loading. Holding until
  // they settle lets the first turn forward the splash's connector selection.
  connectorsLoading: boolean;
  // No agent resolved yet. Every turn names its agent, so the first one waits.
  agentPending: boolean;
  // The conversation already has turns — the composer is live during the wait,
  // so a hand-typed first turn must not get this one stacked on top.
  hasTurns: boolean;
}

// Deliver a conversation's queued New Session first message as its opening turn,
// exactly once. Consumes from the durable queue only at submit — not on mount —
// so leaving before the send (e.g. while connectors load) leaves the message
// queued to deliver on the next mount rather than dropping it. Keying the take
// by conversation id keeps it scoped to this conversation.
//
// The submit is deferred a microtask so it fires after React Strict Mode's
// mount→unmount→mount, rather than being aborted by the mount effect's cleanup
// before its fetch dispatches. The take runs inside the microtask, past the
// `cancelled` check, so a genuine fast unmount leaves the message queued.
export function useFirstMessageDelivery(
  conversationId: string,
  onDeliver: (message: string, attachmentIds: string[]) => void,
  { resumeInFlight, connectorsLoading, agentPending, hasTurns }: DeliveryGates
): void {
  const sentRef = useRef(false);
  useEffect(() => {
    if (sentRef.current || connectorsLoading || agentPending) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled || sentRef.current) return;
      const pending = takePendingFirstMessage(conversationId);
      // resumeInFlight/hasTurns are terminal: drain so the entry can't strand.
      if (!pending || resumeInFlight || hasTurns) return;
      sentRef.current = true;
      onDeliver(pending.message, pending.attachmentIds);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once connectors and the agent settle
  }, [connectorsLoading, agentPending]);
}
